// import Autosuggest from "react-autosuggest";
import * as React from "react";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ButtonState, Model} from "../../types/types";
import {ClientCode, ClientConfig, VirtualServer} from "../../types/clients";
import Autosuggest, {SuggestionsFetchRequestedParams} from "react-autosuggest";
import * as redButtonActions from "../../actions/redbuttons";
import {displayToasts} from "../../actions";
import {createGuid} from "../../guids";
import {validateVirtualServerId} from "../../utils";

interface VirtualServerOption {
  virtualServerId: string
  type: 'Group' | 'Single'
}

type Props = {
  buttonType: 'Suspend' | 'Release'
  code: ClientCode | number
  virtualServerId: string | undefined
}

const greyGradient = (
  <linearGradient id='GrayGradient' x1='100%' y1='40%' x2='0%' y2='0%'>
    <stop offset='0%' stopColor='#FFF' stopOpacity='0.6'/>
    <stop offset='57%' stopColor='#FFF' stopOpacity='0.6'/>
    <stop offset='59%' stopColor='#FFF' stopOpacity='0.4'/>
    <stop offset='100%' stopColor='#FFF' stopOpacity='0.3'/>
  </linearGradient>
)

const buttonShadows =
  <>
    <filter id='drop-shadow' x='0' y='0' width='300%' height='200%'>
      <feOffset result='offOut' in='SourceAlpha' dx='20' dy='20'/>
      <feGaussianBlur result='blurOut' in='offOut' stdDeviation='16'/>
      <feBlend in='SourceGraphic' in2='blurOut' mode='normal'/>
    </filter>
    <filter id='inset-shadow'>
      <feOffset dx='10' dy='6'/>
      <feGaussianBlur stdDeviation='5' result='offset-blur'/>
      <feComposite
        operator='out'
        in='SourceGraphic'
        in2='offset-blur'
        result='inverse'
      />
      <feFlood floodColor='black' floodOpacity='1' result='color'/>
      <feComposite operator='in' in='color' in2='inverse' result='shadow'/>
      <feComponentTransfer in='shadow' result='shadow'>
        <feFuncA type='linear' slope='0.97'/>
      </feComponentTransfer>
      <feComposite operator='over' in='shadow' in2='SourceGraphic'/>
    </filter>
  </>

const shadowForBox = <filter id='inset-shadow-for-box'>
  <feOffset dx='15' dy='15'/>
  <feGaussianBlur stdDeviation='4' result='offset-blur'/>
  <feComposite
    operator='out'
    in='SourceGraphic'
    in2='offset-blur'
    result='inverse'
  />
  <feFlood floodColor='black' floodOpacity='0.9' result='color'/>
  <feComposite operator='in' in='color' in2='inverse' result='shadow'/>
  <feComponentTransfer in='shadow' result='shadow'>
    <feFuncA type='linear' slope='0.97'/>
  </feComponentTransfer>
  <feComposite operator='over' in='shadow' in2='SourceGraphic'/>
</filter>

const blur = <filter id='blur'>
  <feGaussianBlur in='SourceGraphic' stdDeviation='3'/>
</filter>

const redButtonDefs = (
  <defs>
    {buttonShadows}
    {shadowForBox}
    {blur}
    <radialGradient
      id='RedGradient'
      cx='0.25'
      cy='0.25'
      r='0.7'
      fx='0.2'
      fy='0.25'
    >
      <stop offset='0%' stopColor='pink'/>
      <stop offset='35%' stopColor='red'/>
      <stop offset='100%' stopColor='darkred'/>
    </radialGradient>
    <radialGradient
      id='RedGradient2'
      cx='0.25'
      cy='0.25'
      r='0.7'
      fx='0.2'
      fy='0.25'
    >
      <stop offset='0%' stopColor='red'/>
      <stop offset='85%' stopColor='darkred'/>
    </radialGradient>
    {greyGradient}
  </defs>
)

const greenButtonDefs = (
  <defs>
    {buttonShadows}
    {shadowForBox}
    {blur}
    <radialGradient
      id='GreenGradient'
      cx='0.25'
      cy='0.25'
      r='0.7'
      fx='0.2'
      fy='0.25'
    >
      <stop offset='0%' stopColor='lime'/>
      <stop offset='35%' stopColor='green'/>
      <stop offset='100%' stopColor='darkgreen'/>
    </radialGradient>
    <radialGradient
      id='GreenGradient2'
      cx='0.25'
      cy='0.25'
      r='0.7'
      fx='0.2'
      fy='0.25'
    >
      <stop offset='0%' stopColor='#00AA00'/>
      <stop offset='85%' stopColor='darkgreen'/>
    </radialGradient>
    {greyGradient}
  </defs>
)

export default function Button(props: Props) {
  const isRelease = props.buttonType === 'Release';
  const buttonState = useSelector<Model, ButtonState>(m => isRelease ? m.greenButtonState : m.redButtonState);
  const [buttonConfirmationCode, setRedButtonConfirmationCode] = useState<string>('');
  const [suggestions, setSuggestions] = useState<ReadonlyArray<VirtualServerOption>>([]);
  const code = props.code;
  const manualModeActive = typeof code === "number";
  const selectedClient = useSelector<Model, ClientConfig | undefined>(m => m.clientSettings.find(s => s.code === code));
  const virtualServers = selectedClient?.virtualServers || new Set<VirtualServer>();
  const virtualServerGroupId = selectedClient?.virtualServerGroupId;
  const [searchTerm, setSearchTerm] = useState<string>(props.virtualServerId ? props.virtualServerId : virtualServerGroupId ? virtualServerGroupId : '');
  const dispatch = useDispatch();

  const onClick = (value: string) => {
    const virtualServerIds = [...[...virtualServers].map(v => v.serverId), virtualServerGroupId];
    const virtualServerExistsForClient = [...virtualServerIds].some(virtualServerId => virtualServerId === value);

    if (!validateVirtualServerId(value)) {
      dispatch(displayToasts([
        {
          header: "Invalid Virtual Server Id",
          content: `Virtual server ids must be 6 alphanumeric characters. Request was NOT executed.`,
          id: createGuid()
        }]));

      return;
    }

    if (!manualModeActive && !virtualServerExistsForClient) {
      dispatch(displayToasts([
        {
          header: "Unrecognized Virtual Server Id",
          content: `Virtual server ${value} not registered for ${code}. Use manual mode to action this request.`,
          id: createGuid()
        }]));

      return;
    }

    if (isRelease) {
      dispatch(redButtonActions.greenButtonVirtualServer(value));
    } else {
      dispatch(redButtonActions.redButtonVirtualServer(value));
    }
  }

  const setButtonState = (state: ButtonState) => {
    if (isRelease) {
      dispatch(redButtonActions.updateGreenButtonState(state));
    } else {
      dispatch(redButtonActions.updateRedButtonState(state));
    }
  }

  const buttonArmed =
    buttonConfirmationCode !== '' &&
    buttonConfirmationCode.toUpperCase() === code.toString().toUpperCase()


  const buttonEnabled = buttonState !== 'Disabled'
  const color = isRelease ? 'Green' : 'Red';

  const buttonGroup = (
    <g
      transform='translate(250, 600)'
      style={{userSelect: 'none'}}
      className={buttonArmed ? 'clickable hoverable' : undefined}
      onClick={
        buttonEnabled && buttonArmed
          ? () => onClick(searchTerm)
          : undefined
      }
    >
      <circle
        cx={0}
        cy={0}
        r='100'
        strokeWidth='10'
        stroke='#BCC6CC'
        fill='none'
      >
        {buttonEnabled ? null : (
          <animate
            attributeName='stroke'
            attributeType='XML'
            values={`#BCC6CC;${color};#BCC6CC`}
            dur='4s'
            repeatCount='indefinite'
          />
        )}
      </circle>
      <circle
        cx={0}
        cy={0}
        r='95'
        fill={`url(#${color}Gradient)`}
        filter={
          buttonState === 'Depressed' || !buttonEnabled
            ? 'url(#inset-shadow)'
            : 'url(#drop-shadow)'
        }
        onMouseDown={
          buttonEnabled && buttonArmed ? () => setButtonState('Depressed') : undefined
        }
        onMouseUp={
          buttonEnabled && buttonArmed ? () => setButtonState('Raised') : undefined
        }
        onMouseOut={
          buttonEnabled && buttonArmed ? () => setButtonState('Raised') : undefined
        }
      />
    </g>
  )

  const strokeColor = props.buttonType === "Release" ? '#00AA00' : '#BB0000';

  const calculateSuggestions = (request: SuggestionsFetchRequestedParams) => {
    const value = request.value.trim().toUpperCase();
    const matchingServers: ReadonlyArray<VirtualServer> = !value ? [...virtualServers] : [...virtualServers].filter(v => v.serverId.startsWith(value))
    let newSuggestions: VirtualServerOption[] = matchingServers.map(m => {
      return {virtualServerId: m.serverId, type: "Single"}
    });

    if (virtualServerGroupId && (virtualServerGroupId?.startsWith(value) || !value)) {
      newSuggestions = [{virtualServerId: virtualServerGroupId, type: "Group"}, ...newSuggestions];
    }

    setSuggestions(newSuggestions.slice(0, 20));
  }

  const virtualServer = <foreignObject x='100' y='250' width='300' height='230'>
    <Autosuggest
      suggestions={suggestions}
      renderSuggestion={suggestion => `${suggestion.virtualServerId} - ${suggestion.type}`}
      onSuggestionsFetchRequested={calculateSuggestions}
      onSuggestionsClearRequested={() => setSuggestions([])}
      getSuggestionValue={suggestion => suggestion.virtualServerId}
      shouldRenderSuggestions={() => true}
      inputProps={{
        value: searchTerm,
        onChange: (event: React.FormEvent<any>, params: Autosuggest.ChangeEvent) => setSearchTerm(params.newValue),
        maxLength: 6,
        placeholder: "Virtual Server Id"
      }}
    />
  </foreignObject>;

  const groupText = searchTerm === virtualServerGroupId ? <tspan className='dangerText'>group</tspan> : null;

  const greenButtonText = <text
    y='340'
    className='infoText'
    textAnchor='middle'
    alignmentBaseline='central'
  >
    <tspan x='250' dy='1.2em'>
      Lift Suspension on {manualModeActive ? searchTerm : `${code}'s`}
    </tspan>
    <tspan x='250' dy='1.2em'>
      TSE virtual server {groupText}
    </tspan>
  </text>;

  const redButtonText = <text
    y='340'
    className='infoText'
    textAnchor='middle'
    alignmentBaseline='central'
  >
    <tspan x='250' dy='1.2em'>
      Suspend TSE virtual server {searchTerm === virtualServerGroupId ? <tspan className='dangerText'>group</tspan> : null}
    </tspan>
    <tspan x='250' dy='1.2em'>
      and cancel any open orders
    </tspan>
  </text>


  return (
    <svg height='800' width='500' role='button'>
      {isRelease ? greenButtonDefs : redButtonDefs}
      <rect
        height='800'
        width='500'
        fill='none'
        stroke={strokeColor}
        strokeWidth='30'
      />
      <rect
        height='770'
        width='470'
        x='15'
        y='15'
        fill={`url(#${color}Gradient2)`}
        filter='url(#inset-shadow-for-box)'
      />
      {buttonGroup}
      {!buttonArmed ? virtualServer : null}
      <g filter={buttonArmed ? undefined : 'url(#blur)'}>
        {isRelease ? greenButtonText : redButtonText}
      </g>
      <rect
        height='770'
        width='470'
        x='15'
        y='15'
        fill={buttonArmed ? 'none' : 'url(#GrayGradient)'}
      />
      <text
        x='250'
        y='100'
        className='emergencyText'
        textAnchor='middle'
        alignmentBaseline='central'
      >
        IN CASE OF BREACH
      </text>
      <text
        x='250'
        y='150'
        className='emergencyText'
        textAnchor='middle'
        alignmentBaseline='central'
      >
        {manualModeActive ? `ENTER ${code}` : 'ENTER CUSTOMER CODE'}
      </text>
      <foreignObject x='100' y='200' width='300' height='120'>
        <input
          className='customer-code-input input'
          tabIndex={0}
          value={buttonConfirmationCode}
          onChange={(event) => setRedButtonConfirmationCode(event.currentTarget.value)}
          placeholder="Code"
        />
      </foreignObject>
      {buttonArmed ? virtualServer : null
      }
    </svg>
  )
}
